import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Star, Clock } from "lucide-react";
import { useConsultationExperts } from "@/hooks/use-consultation-experts";

export function ExpertCallsSection() {
  const navigate = useNavigate();
  const { data: experts, isLoading } = useConsultationExperts();

  // Get the first 3 experts for the homepage
  const featuredExperts = experts?.slice(0, 3) || [];

  const useCases = [
    { icon: "📋", title: "Fair Deal Applications", description: "Navigate the application process with expert guidance" },
    { icon: "🚨", title: "Urgent Placement", description: "Quick decisions when time is critical" },
    { icon: "📅", title: "Planning Ahead", description: "Prepare for future care needs thoughtfully" }
  ];

  if (isLoading) {
    return (
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Talk to a Real Expert Today
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Get personalized guidance from verified care placement experts who understand the Irish healthcare system
          </p>
          
          {/* Use Cases */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            {useCases.map((useCase, index) => (
              <div key={index} className="text-center p-4">
                <div className="text-3xl mb-2">{useCase.icon}</div>
                <h3 className="font-semibold text-gray-900 mb-2">{useCase.title}</h3>
                <p className="text-sm text-gray-600">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>

        {featuredExperts.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto mb-8">
            {featuredExperts.map((expert) => (
              <Card key={expert.id} className="h-full hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <img 
                      src={expert.profile_image_url || "/lovable-uploads/e7b5d364-ad74-4341-aa95-ba4e6d7d1836.png"} 
                      alt={expert.full_name}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{expert.full_name}</h3>
                      <p className="text-grandpal-green-dark text-sm font-medium">{expert.professional_title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {expert.rating && expert.review_count && (
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            <span className="text-xs font-medium">{expert.rating}</span>
                          </div>
                        )}
                        <span className="text-xs text-gray-600">{expert.years_experience}+ years</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-grandpal-green">€{expert.hourly_rate}</p>
                      <p className="text-xs text-gray-600">per hour</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mb-4">
                    {expert.specializations.slice(0, 2).map((specialty, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {specialty}
                      </Badge>
                    ))}
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {expert.short_description || expert.bio}
                  </p>
                  
                  <Button 
                    size="sm"
                    className="w-full bg-grandpal-green hover:bg-grandpal-green/90 text-white"
                    onClick={() => {
                      if (expert.google_calendar_link) {
                        window.open(expert.google_calendar_link, '_blank');
                      } else {
                        navigate('/consultations');
                      }
                    }}
                  >
                    Book Consultation
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center mb-8">
            <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Experts Coming Soon</h3>
              <p className="text-gray-600 mb-4">
                We're bringing verified care experts to help you navigate your options.
              </p>
              <Button 
                variant="outline"
                onClick={() => navigate('/consultations')}
              >
                Learn More
              </Button>
            </div>
          </div>
        )}

        <div className="text-center">
          <Button 
            size="lg"
            variant="outline"
            className="border-grandpal-green text-grandpal-green hover:bg-grandpal-green/10 px-8"
            onClick={() => navigate('/consultations')}
          >
            View All Experts
          </Button>
        </div>
      </div>
    </section>
  );
}